// Cash drawer, cash journal and staff permissions: the arithmetic and rules shared by the API and the screens.

export const DENOMINATIONS = [
  { key: 'hundreds', label: '$100', cents: 10000 }, { key: 'fifties', label: '$50', cents: 5000 }, { key: 'twenties', label: '$20', cents: 2000 },
  { key: 'tens', label: '$10', cents: 1000 }, { key: 'fives', label: '$5', cents: 500 }, { key: 'twos', label: '$2', cents: 200 }, { key: 'ones', label: '$1', cents: 100 },
  { key: 'quarters', label: '25¢', cents: 25 }, { key: 'dimes', label: '10¢', cents: 10 }, { key: 'nickels', label: '5¢', cents: 5 }, { key: 'pennies', label: '1¢', cents: 1 },
] as const;
export type Counts = Partial<Record<(typeof DENOMINATIONS)[number]['key'], number>>;

/** Cash in the drawer from a count of notes and coins, plus any rolled coin or other amount typed in as a total. */
export function countCash(counts: Counts, otherCents = 0): number {
  let total = Math.round(otherCents);
  for (const d of DENOMINATIONS) {
    const n = counts[d.key] ?? 0;
    if (!Number.isInteger(n) || n < 0) throw new Error(`Count for ${d.label} must be a whole number`);
    total += n * d.cents;
  }
  return total;
}

export interface CashFlows {
  openingCents: number;
  cashSalesCents: number;        // cash taken for sales, already net of change given (negative for cash refunds)
  lotterySalesCents: number;
  lotteryPayoutsCents: number;
  giftCardCashCents: number;     // gift cards sold for cash
  paidInCents: number;
  paidOutCents: number;          // includes safe drops and bank deposits taken from the drawer
}

/** Cash that should be in the drawer. Lottery payouts and every paid-out leave the drawer; lottery sales and gift cards sold for cash come in. */
export function expectedCash(f: CashFlows): number {
  return f.openingCents + f.cashSalesCents + f.lotterySalesCents - f.lotteryPayoutsCents + f.giftCardCashCents + f.paidInCents - f.paidOutCents;
}

export type CashMove = { kind: 'in'; reason: 'from_safe' | 'owner' } | { kind: 'out'; reason: 'expense' | 'vendor' | 'owner_draw' | 'safe_drop' | 'bank_deposit' };
export const MOVE_REASONS: { kind: 'in' | 'out'; reason: string; label: string }[] = [
  { kind: 'out', reason: 'safe_drop', label: 'Safe drop (cash to the safe)' }, { kind: 'out', reason: 'bank_deposit', label: 'Bank deposit' },
  { kind: 'out', reason: 'expense', label: 'Store expense (supplies, repairs)' }, { kind: 'out', reason: 'vendor', label: 'Paid a vendor (reduces what you owe)' }, { kind: 'out', reason: 'owner_draw', label: "Owner's draw" },
  { kind: 'in', reason: 'from_safe', label: 'Cash from the safe (change fund)' }, { kind: 'in', reason: 'owner', label: "Owner's contribution" },
];

// ---------------- permissions ----------------
export const PERMISSIONS = [
  { key: 'refund', label: 'Refund or return items' }, { key: 'void_sale', label: 'Void sales and lottery entries' }, { key: 'price_override', label: 'Change prices and give discounts at the register' },
  { key: 'cash_out', label: 'Paid out, vendor payments, bank deposits' }, { key: 'cash_in', label: 'Paid in' }, { key: 'close_any_drawer', label: "Close another cashier's drawer" },
  { key: 'close_day', label: 'Finish the day' }, { key: 'view_cashier_reports', label: 'See cashier reports and commissions' },
  { key: 'manage_items', label: 'Add and change items and prices' }, { key: 'adjust_stock', label: 'Adjust stock and run counts' },
] as const;
export type Permission = (typeof PERMISSIONS)[number]['key'];
export type Role = 'cashier' | 'manager' | 'owner' | 'platform_admin';

const MANAGER: Permission[] = ['refund', 'void_sale', 'price_override', 'cash_out', 'cash_in', 'close_any_drawer', 'close_day', 'view_cashier_reports', 'manage_items', 'adjust_stock'];
export const ROLE_DEFAULTS: Record<Role, ReadonlySet<Permission>> = {
  cashier: new Set<Permission>([]),
  manager: new Set<Permission>(MANAGER),
  owner: new Set<Permission>(PERMISSIONS.map((p) => p.key)),
  platform_admin: new Set<Permission>(PERMISSIONS.map((p) => p.key)),
};

/** Owners can always do everything. Otherwise a per-person setting wins over the role's default. */
export function can(role: Role, overrides: Partial<Record<Permission, boolean>> | null | undefined, key: Permission): boolean {
  if (role === 'owner' || role === 'platform_admin') return true;
  const o = overrides?.[key];
  return o !== undefined ? o : ROLE_DEFAULTS[role].has(key);
}
